import { getEnvironment, isEnvironment } from './environment';
import cloneDeep from 'lodash/cloneDeep';
import { getRegionShortName, getRegionSuffix } from './getRegion';
import merge from 'lodash/merge';
import { Environments } from '../types/environments.types';
import { generateWlc360iHostUrl, generateWlc360iUrl, } from './generateWlc360iUrl';
const generateAssetUrl = (subdomain, env, stage) => {
    const envLetter = getUrlEnvLetterFromEnv(env);
    const regionShortName = getRegionShortName();
    return `https://${subdomain}.wlc360a-${envLetter}-${regionShortName}.autodesk.com/${stage}`;
};
const generateAssetWssUrl = (subdomain, env, stage) => generateAssetUrl(subdomain, env, stage).replace(/^https:/, 'wss:');
const generateWaterAIUrl = (subdomain, env, stage) => {
    const envLetter = getUrlEnvLetterFromEnv(env);
    const regionShortName = getRegionShortName();
    return `https://${subdomain}.wlc360ai-${envLetter}-${regionShortName}.autodesk.com/${stage}`;
};
export const generateWaterAIUrlCloudOs = (subdomain, env) => {
    const envLetter = getUrlEnvLetterFromEnv(env);
    const regionShortName = getRegionShortName();
    return `https://${subdomain}.wlc360ai-${envLetter}-${regionShortName}.cloudos.autodesk.com`;
};
export const generateStandardUrl = (subDomain, env) => {
    const region = getRegionSuffix();
    return `https://${subDomain}.info360${region ? '-' + region : ''}-${env === 'prod' ? 'prd' : env}.com/${env}`;
};
export const removePathPartFromUrl = (url) => {
    const urlParts = url.split('/');
    urlParts.pop();
    return urlParts.join('/');
};
export const generateNotificationHubAppsyncUrl = (subdomain) => {
    const region = getRegionSuffix();
    return `https://${subdomain}.info360${region ? '-' + region : ''}-prd.com/graphql`;
};
const getUrlEnvLetterFromEnv = (env) => {
    switch (env) {
        case Environments.LOCAL:
            return 'c';
        case Environments.DEV:
            return 'c';
        case Environments.QA:
            return 's';
        case Environments.STAGE:
            return 's';
        case Environments.PROD:
            return 'p';
    }
};
export const generatePlatformUrl = (subdomain, env, stage) => {
    const regionShortName = getRegionShortName();
    const envTag = getUrlEnvLetterFromEnv(env);
    //example: https://saas-notification-hub-prd-prd-appsync.saas-notification-hub.wlc360p-p-uw2.autodesk.com
    return (`https://${subdomain}.wlc360p-${envTag}-${regionShortName}.autodesk.com` + (stage ? `/${stage}` : ''));
};
export const generateTilesUrl = (subdomain, env, stage) => {
    const envLetter = getUrlEnvLetterFromEnv(env);
    const regionShortName = getRegionShortName();
    return `https://${subdomain}.wlc360p-${envLetter}-${regionShortName}.autodesk.com/${stage}`;
};
export const generateDeviantUrl = (url) => {
    const regionRegex = /\|region\|/g;
    const region = getRegionSuffix();
    return region
        ? url.replace(regionRegex, '-' + region)
        : url.replace(regionRegex, '');
};
export const generateRegionForcedStandardUrl = (region, subDomain, env) => `https://${subDomain}.info360${region === 'eu' || region === 'ap' ? '-' + region : ''}-${env === 'prod' ? 'prd' : env}.com/${env}`;
const ANALYSIS_PROD_REGION_BY_SUFFIX = {
    '': 'usa',
    eu: 'gbr',
    ap: 'aus',
};
// The global modern staging host (api.aps.stg.autodesk.com) requires an
// x-ads-region header; the USA-regional staging host doesn't need one at all
// (confirmed by APS eng in Slack on 2026-09-16), so DEV/STG resolve through
// that regional host instead.
const generateAnalysisUrl = (stage) => {
    if (stage === 'prd') {
        const region = ANALYSIS_PROD_REGION_BY_SUFFIX[getRegionSuffix()] ?? 'usa';
        return `https://api.aps.${region}.autodesk.com/water/analysis/v3`;
    }
    return `https://api.aps.stg.usa.autodesk.com/water/analysis${stage === 'dev' ? '-dev' : ''}/v3`;
};
export const STANDARD_LOCAL_URL = 'http://localhost:3276/dev';
/** generate feature flagged urls
 * @param featureFlags - feature flags object
 * @param ffUrls - array of tuples containing feature flag and url
 * @param fallbackUrl - fallback url if no feature flag is enabled
 * @return url string
 *
 * @example
 * const featureFlags = {
 *   releaseMapManagementCloudOs: true,
 *  // other flags...
 * };
 * const ffUrls = [
 *  ['i-360-release-map-management-cloudos', 'https://example.com'],
 * ];
 * const fallbackUrl = 'https://fallback.com';
 * const url = useFFUrl(featureFlags, ffUrls, fallbackUrl);
 */
export const useFFUrl = (featureFlags, ffUrls, fallbackUrl) => {
    for (const [ff, url] of ffUrls) {
        if (featureFlags[ff]) {
            return url;
        }
    }
    return fallbackUrl;
};
const serviceMap = (featureFlags = {}) => ({
    [Environments.LOCAL]: {
        alertsManager: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsManager',
                generateWlc360iUrl('saas-alerts-management', 'dev'),
            ],
        ], generateStandardUrl('saas-alerts-management', 'dev')),
        alertsScheduler: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsScheduler',
                generateWlc360iUrl('saas-alerts-scheduler', 'dev'),
            ],
        ], generateStandardUrl('saas-alerts-scheduler', 'dev')),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'dev'),
        assets: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-asset-management', 'dev')),
        assetsV2: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.LOCAL, 'dev').concat('/v2'),
            ],
        ], generateStandardUrl('saas-asset-management', 'dev').concat('/v2')),
        billing: generateStandardUrl('saas-billing', 'dev'),
        edgeManager: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-edgeManager',
                generatePlatformUrl('saas-edge-manager', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-edge-manager', 'dev')),
        entitlements: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-entitlements',
                generatePlatformUrl('saas-auth', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-auth', 'dev')),
        iliEngine: generateStandardUrl('saas-ili-engine', 'dev'),
        iliManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_ili_management', // feature flag name
                generateWlc360iUrl('saas-ili-management', 'dev'),
            ],
        ], generateStandardUrl('saas-ili-management', 'dev')),
        incident: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-incident',
                generateWlc360iUrl('saas-incident-management', 'dev'),
            ],
        ], generateStandardUrl('saas-incident-management', 'dev')),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-dev.com/dev/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'dev'),
        massBalance: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_mass_balance',
                generateWlc360iUrl('saas-mass-balance', 'dev'),
            ],
        ], generateStandardUrl('saas-mass-balance', 'dev')),
        rules: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_rule_management',
                generateWlc360iUrl('saas-rule-management', 'dev'),
            ],
        ], generateStandardUrl('saas-rule-management', 'dev')),
        sensors: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-sensors',
                generateWlc360iUrl('saas-sensor', 'dev'),
            ],
        ], generateStandardUrl('saas-sensor', 'dev')),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-dev.com/dev/'),
        workspaces: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-workspaces',
                generateWlc360iUrl('saas-workspace', 'dev'),
            ],
        ], generateStandardUrl('saas-workspace', 'dev')),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles-dev', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('tiles-dev', 'dev')),
        amUpload: generateStandardUrl('saas-upload-service', 'dev'),
        amInspection: generateStandardUrl('saas-inspection-management', 'dev'),
        superAdmin: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-superAdmin',
                generatePlatformUrl('saas-superadmin', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-superadmin', 'dev')),
        amRisk: generateStandardUrl('saas-risk-management', 'dev'),
        amRiskWss: useFFUrl(featureFlags, [
            [
                'use_wlc360a_saas_asset_web_socket',
                generateAssetWssUrl('saas-asset-web-socket', Environments.LOCAL, 'dev'),
            ],
        ], generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-dev.com/dev')),
        dataAuthorizer: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data',
                generateWlc360iUrl('saas-info360-sensor-data', 'dev'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev')),
        sensorData: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iHostUrl('saas-info360-sensor-data', 'dev'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com')),
        sensorDataV2: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iUrl('saas-info360-sensor-data', 'dev'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev')),
        valueMapping: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_value_mapping',
                generatePlatformUrl('saas-value-mapping', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-value-mapping', 'dev')),
        sensorGroup: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_sensor_group', // feature flag name
                generateWlc360iUrl('saas-sensor-group', 'dev'),
            ],
        ], generateStandardUrl('saas-sensor-group', 'dev')),
        liftStations: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-liftStations',
                generateWlc360iUrl('saas-lift-stations', 'dev'),
            ],
        ], generateStandardUrl('saas-lift-stations', 'dev')),
        customAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_custom_analytics',
                generateWlc360iUrl('saas-custom-analytics', 'dev'),
            ],
        ], generateStandardUrl('saas-custom-analytics', 'dev')),
        patterns: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-patterns',
                generateWlc360iUrl('saas-pattern', 'dev'),
            ],
        ], generateStandardUrl('saas-pattern', 'dev')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'dev')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.LOCAL, 'dev'),
        export: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_export_service',
                generatePlatformUrl('saas-export-service', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-export-service', 'dev')),
        imageManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_image_management', // feature flag name
                generateWlc360iUrl('saas-image-management', 'dev'),
            ],
        ], generateStandardUrl('saas-image-management', 'dev')),
        idp: generateStandardUrl('saas-idp-router', 'dev'),
        serviceIntegration: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_serviceintegration',
                generatePlatformUrl('saas-serviceintegration', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-serviceintegration', 'dev')),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-map-management', 'dev')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'dev')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'dev'),
        sharedTemplates: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'dev'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'dev')),
        sharedTemplatesProxy: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'dev'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'dev')),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'dev'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'dev'),
            ],
        ], generateStandardUrl('saas-favorites', 'dev')),
        userManagement: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-userManagement',
                generatePlatformUrl('saas-user-management', Environments.LOCAL, 'dev'),
            ],
            [
                'use-saas-user-management-backend',
                generatePlatformUrl('saas-user-management', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-user-management', 'dev')),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-dev-dev.info360-dev.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-dev-dev-appsync.saas-notification-hub.wlc360p-c-uw2.autodesk.com/graphql',
        edgeAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_edge_analytics',
                generatePlatformUrl('saas-edge-analytics', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-edge-analytics', 'dev')),
        goals: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_goal_management',
                generateWlc360iUrl('saas-goal-management', 'dev'),
            ],
        ], generateStandardUrl('saas-goal-management', 'dev')),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-dev.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'dev'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'dev'),
        modelFactoryCos: generateWaterAIUrl('saas-water-ai-model-factory', Environments.LOCAL, 'dev'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'dev'),
        simulationResults: generateStandardUrl('saas-simulation-results-dev', 'dev'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.LOCAL, 'dev'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.LOCAL, 'dev'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.LOCAL),
        amso: generateWaterAIUrlCloudOs('amso', Environments.LOCAL),
        analysis: generateAnalysisUrl('dev'),
    },
    [Environments.DEV]: {
        alertsManager: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsManager',
                generateWlc360iUrl('saas-alerts-management', 'dev'),
            ],
        ], generateStandardUrl('saas-alerts-management', 'dev')),
        alertsScheduler: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsScheduler',
                generateWlc360iUrl('saas-alerts-scheduler', 'dev'),
            ],
        ], generateStandardUrl('saas-alerts-scheduler', 'dev')),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'dev'),
        assets: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-asset-management', 'dev')),
        assetsV2: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.DEV, 'dev').concat('/v2'),
            ],
        ], generateStandardUrl('saas-asset-management', 'dev').concat('/v2')),
        billing: generateStandardUrl('saas-billing', 'dev'),
        edgeManager: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-edgeManager',
                generatePlatformUrl('saas-edge-manager', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-edge-manager', 'dev')),
        entitlements: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-entitlements',
                generatePlatformUrl('saas-auth', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-auth', 'dev')),
        iliEngine: generateStandardUrl('saas-ili-engine', 'dev'),
        iliManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_ili_management', // feature flag name
                generateWlc360iUrl('saas-ili-management', 'dev'),
            ],
        ], generateStandardUrl('saas-ili-management', 'dev')),
        incident: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-incident',
                generateWlc360iUrl('saas-incident-management', 'dev'),
            ],
        ], generateStandardUrl('saas-incident-management', 'dev')),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-dev.com/dev/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'dev'),
        massBalance: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_mass_balance',
                generateWlc360iUrl('saas-mass-balance', 'dev'),
            ],
        ], generateStandardUrl('saas-mass-balance', 'dev')),
        rules: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_rule_management',
                generateWlc360iUrl('saas-rule-management', 'dev'),
            ],
        ], generateStandardUrl('saas-rule-management', 'dev')),
        sensors: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-sensors',
                generateWlc360iUrl('saas-sensor', 'dev'),
            ],
        ], generateStandardUrl('saas-sensor', 'dev')),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-dev.com/dev/'),
        workspaces: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-workspaces',
                generateWlc360iUrl('saas-workspace', 'dev'),
            ],
        ], generateStandardUrl('saas-workspace', 'dev')),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles-dev', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('tiles-dev', 'dev')),
        amUpload: generateStandardUrl('saas-upload-service', 'dev'),
        amInspection: generateStandardUrl('saas-inspection-management', 'dev'),
        superAdmin: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-superAdmin',
                generatePlatformUrl('saas-superadmin', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-superadmin', 'dev')),
        amRisk: generateStandardUrl('saas-risk-management', 'dev'),
        amRiskWss: useFFUrl(featureFlags, [
            [
                'use_wlc360a_saas_asset_web_socket',
                generateAssetWssUrl('saas-asset-web-socket', Environments.DEV, 'dev'),
            ],
        ], generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-dev.com/dev')),
        dataAuthorizer: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data',
                generateWlc360iUrl('saas-info360-sensor-data', 'dev'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev')),
        sensorData: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iHostUrl('saas-info360-sensor-data', 'dev'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com')),
        sensorDataV2: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iUrl('saas-info360-sensor-data', 'dev'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev')),
        valueMapping: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_value_mapping',
                generatePlatformUrl('saas-value-mapping', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-value-mapping', 'dev')),
        sensorGroup: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_sensor_group', // feature flag name
                generateWlc360iUrl('saas-sensor-group', 'dev'),
            ],
        ], generateStandardUrl('saas-sensor-group', 'dev')),
        liftStations: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-liftStations',
                generateWlc360iUrl('saas-lift-stations', 'dev'),
            ],
        ], generateStandardUrl('saas-lift-stations', 'dev')),
        customAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_custom_analytics',
                generateWlc360iUrl('saas-custom-analytics', 'dev'),
            ],
        ], generateStandardUrl('saas-custom-analytics', 'dev')),
        patterns: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-patterns',
                generateWlc360iUrl('saas-pattern', 'dev'),
            ],
        ], generateStandardUrl('saas-pattern', 'dev')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'dev')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.DEV, 'dev'),
        export: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_export_service',
                generatePlatformUrl('saas-export-service', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-export-service', 'dev')),
        imageManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_image_management', // feature flag name
                generateWlc360iUrl('saas-image-management', 'dev'),
            ],
        ], generateStandardUrl('saas-image-management', 'dev')),
        idp: generateStandardUrl('saas-idp-router', 'dev'),
        serviceIntegration: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_serviceintegration',
                generatePlatformUrl('saas-serviceintegration', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-serviceintegration', 'dev')),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-map-management', 'dev')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'dev')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'dev'),
        sharedTemplates: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'dev'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'dev')),
        sharedTemplatesProxy: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'dev'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'dev')),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'dev'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'dev'),
            ],
        ], generateStandardUrl('saas-favorites', 'dev')),
        userManagement: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-userManagement',
                generatePlatformUrl('saas-user-management', Environments.DEV, 'dev'),
            ],
            [
                'use-saas-user-management-backend',
                generatePlatformUrl('saas-user-management', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-user-management', 'dev')),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-dev-dev.info360-dev.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-dev-dev-appsync.saas-notification-hub.wlc360p-c-uw2.autodesk.com/graphql',
        edgeAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_edge_analytics',
                generatePlatformUrl('saas-edge-analytics', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-edge-analytics', 'dev')),
        goals: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_goal_management',
                generateWlc360iUrl('saas-goal-management', 'dev'),
            ],
        ], generateStandardUrl('saas-goal-management', 'dev')),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-dev.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'dev'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'dev'),
        modelFactoryCos: generateWaterAIUrl('saas-water-ai-model-factory', Environments.DEV, 'dev'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'dev'),
        simulationResults: generateStandardUrl('saas-simulation-results-dev', 'dev'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.DEV, 'dev'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.DEV, 'dev'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.DEV),
        amso: generateWaterAIUrlCloudOs('amso', Environments.DEV),
        analysis: generateAnalysisUrl('dev'),
    },
    [Environments.QA]: {
        alertsManager: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsManager',
                generateWlc360iUrl('saas-alerts-management', 'qa'),
            ],
        ], generateStandardUrl('saas-alerts-management', 'qa')),
        alertsScheduler: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsScheduler',
                generateWlc360iUrl('saas-alerts-scheduler', 'qa'),
            ],
        ], generateStandardUrl('saas-alerts-scheduler', 'qa')),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'qa'),
        assets: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-asset-management', 'qa')),
        assetsV2: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.QA, 'stg').concat('/v2'),
            ],
        ], generateStandardUrl('saas-asset-management', 'qa').concat('/v2')),
        billing: generateStandardUrl('saas-billing', 'qa'),
        edgeManager: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-edgeManager',
                generatePlatformUrl('saas-edge-manager', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-edge-manager', 'qa')),
        entitlements: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-entitlements',
                generatePlatformUrl('saas-auth', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-auth', 'qa')),
        iliEngine: generateStandardUrl('saas-ili-engine', 'qa'),
        iliManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_ili_management', // feature flag name
                generateWlc360iUrl('saas-ili-management', 'qa'),
            ],
        ], generateStandardUrl('saas-ili-management', 'qa')),
        incident: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-incident',
                generateWlc360iUrl('saas-incident-management', 'qa'),
            ],
        ], generateStandardUrl('saas-incident-management', 'qa')),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-qa.com/qa/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'qa'),
        massBalance: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_mass_balance',
                generateWlc360iUrl('saas-mass-balance', 'qa'),
            ],
        ], generateStandardUrl('saas-mass-balance', 'qa')),
        rules: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_rule_management',
                generateWlc360iUrl('saas-rule-management', 'qa'),
            ],
        ], generateStandardUrl('saas-rule-management', 'qa')),
        sensors: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-sensors',
                generateWlc360iUrl('saas-sensor', 'qa'),
            ],
        ], generateStandardUrl('saas-sensor', 'qa')),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-qa.com/qa/'),
        workspaces: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-workspaces',
                generateWlc360iUrl('saas-workspace', 'qa'),
            ],
        ], generateStandardUrl('saas-workspace', 'qa')),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('tiles', 'qa')),
        amUpload: generateStandardUrl('saas-upload-service', 'qa'),
        amInspection: generateStandardUrl('saas-inspection-management', 'qa'),
        superAdmin: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-superAdmin',
                generatePlatformUrl('saas-superadmin', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-superadmin', 'qa')),
        amRisk: generateStandardUrl('saas-risk-management', 'qa'),
        amRiskWss: useFFUrl(featureFlags, [
            [
                'use_wlc360a_saas_asset_web_socket',
                generateAssetWssUrl('saas-asset-web-socket', Environments.QA, 'stg'),
            ],
        ], generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-qa.com/qa')),
        dataAuthorizer: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data',
                generateWlc360iUrl('saas-info360-sensor-data', 'qa'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa')),
        sensorData: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iHostUrl('saas-info360-sensor-data', 'qa'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com')),
        sensorDataV2: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iUrl('saas-info360-sensor-data', 'qa'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa')),
        valueMapping: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_value_mapping',
                generatePlatformUrl('saas-value-mapping', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-value-mapping', 'qa')),
        sensorGroup: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_sensor_group', // feature flag name
                generateWlc360iUrl('saas-sensor-group', 'qa'),
            ],
        ], generateStandardUrl('saas-sensor-group', 'qa')),
        liftStations: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-liftStations',
                generateWlc360iUrl('saas-lift-stations', 'qa'),
            ],
        ], generateStandardUrl('saas-lift-stations', 'qa')),
        customAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_custom_analytics',
                generateWlc360iUrl('saas-custom-analytics', 'qa'),
            ],
        ], generateStandardUrl('saas-custom-analytics', 'qa')),
        // autodeskUser: generateStandardUrl('saas-auth', 'qa'),
        patterns: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-patterns',
                generateWlc360iUrl('saas-pattern', 'stg'),
            ],
        ], generateStandardUrl('saas-pattern', 'qa')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'qa')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.QA, 'stg'),
        export: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_export_service',
                generatePlatformUrl('saas-export-service', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-export-service', 'qa')),
        imageManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_image_management', // feature flag name
                generateWlc360iUrl('saas-image-management', 'qa'),
            ],
        ], generateStandardUrl('saas-image-management', 'qa')),
        idp: generateStandardUrl('saas-idp-router', 'qa'),
        serviceIntegration: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_serviceintegration',
                generatePlatformUrl('saas-serviceintegration', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-serviceintegration', 'qa')),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-map-management', 'qa')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'qa')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'qa'),
        sharedTemplates: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'qa'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'qa')),
        sharedTemplatesProxy: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'qa'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'qa')),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'qa'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'qa'),
            ],
        ], generateStandardUrl('saas-favorites', 'qa')),
        userManagement: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-userManagement',
                generatePlatformUrl('saas-user-management', Environments.QA, 'stg'),
            ],
            [
                'use-saas-user-management-backend',
                generatePlatformUrl('saas-user-management', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-user-management', 'qa')),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-qa-qa.info360-qa.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-stg-stg-appsync.saas-notification-hub.wlc360p-s-uw2.autodesk.com/graphql',
        edgeAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_edge_analytics',
                generatePlatformUrl('saas-edge-analytics', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-edge-analytics', 'qa')),
        goals: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_goal_management',
                generateWlc360iUrl('saas-goal-management', 'qa'),
            ],
        ], generateStandardUrl('saas-goal-management', 'qa')),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-qa.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'qa'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'qa'),
        modelFactoryCos: generateWaterAIUrl('saas-water-ai-model-factory', Environments.QA, 'stg'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'qa'),
        simulationResults: generateStandardUrl('saas-simulation-results', 'qa'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.QA, 'stg'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.QA, 'stg'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.QA),
        amso: generateWaterAIUrlCloudOs('amso', Environments.QA),
        analysis: generateAnalysisUrl('stg'),
    },
    [Environments.STAGE]: {
        alertsManager: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsManager',
                generateWlc360iUrl('saas-alerts-management', 'qa'),
            ],
        ], generateStandardUrl('saas-alerts-management', 'qa')),
        alertsScheduler: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsScheduler',
                generateWlc360iUrl('saas-alerts-scheduler', 'qa'),
            ],
        ], generateStandardUrl('saas-alerts-scheduler', 'qa')),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'qa'),
        assets: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-asset-management', 'qa')),
        assetsV2: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.STAGE, 'stg').concat('/v2'),
            ],
        ], generateStandardUrl('saas-asset-management', 'qa').concat('/v2')),
        billing: generateStandardUrl('saas-billing', 'qa'),
        edgeManager: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-edgeManager',
                generatePlatformUrl('saas-edge-manager', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-edge-manager', 'qa')),
        entitlements: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-entitlements',
                generatePlatformUrl('saas-auth', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-auth', 'qa')),
        iliEngine: generateStandardUrl('saas-ili-engine', 'qa'),
        iliManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_ili_management', // feature flag name
                generateWlc360iUrl('saas-ili-management', 'qa'),
            ],
        ], generateStandardUrl('saas-ili-management', 'qa')),
        incident: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-incident',
                generateWlc360iUrl('saas-incident-management', 'qa'),
            ],
        ], generateStandardUrl('saas-incident-management', 'qa')),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-qa.com/qa/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'qa'),
        massBalance: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_mass_balance',
                generateWlc360iUrl('saas-mass-balance', 'qa'),
            ],
        ], generateStandardUrl('saas-mass-balance', 'qa')),
        rules: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_rule_management',
                generateWlc360iUrl('saas-rule-management', 'qa'),
            ],
        ], generateStandardUrl('saas-rule-management', 'qa')),
        sensors: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-sensors',
                generateWlc360iUrl('saas-sensor', 'qa'),
            ],
        ], generateStandardUrl('saas-sensor', 'qa')),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-qa.com/qa/'),
        workspaces: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-workspaces',
                generateWlc360iUrl('saas-workspace', 'stg'),
            ],
        ], generateStandardUrl('saas-workspace', 'qa')),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('tiles', 'qa')),
        amUpload: generateStandardUrl('saas-upload-service', 'qa'),
        amInspection: generateStandardUrl('saas-inspection-management', 'qa'),
        superAdmin: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-superAdmin',
                generatePlatformUrl('saas-superadmin', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-superadmin', 'qa')),
        amRisk: generateStandardUrl('saas-risk-management', 'qa'),
        amRiskWss: useFFUrl(featureFlags, [
            [
                'use_wlc360a_saas_asset_web_socket',
                generateAssetWssUrl('saas-asset-web-socket', Environments.STAGE, 'stg'),
            ],
        ], generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-qa.com/qa')),
        dataAuthorizer: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data',
                generateWlc360iUrl('saas-info360-sensor-data', 'qa'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa')),
        sensorData: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iHostUrl('saas-info360-sensor-data', 'qa'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com')),
        sensorDataV2: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iUrl('saas-info360-sensor-data', 'qa'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa')),
        valueMapping: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_value_mapping',
                generatePlatformUrl('saas-value-mapping', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-value-mapping', 'qa')),
        sensorGroup: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_sensor_group', // feature flag name
                generateWlc360iUrl('saas-sensor-group', 'qa'),
            ],
        ], generateStandardUrl('saas-sensor-group', 'qa')),
        liftStations: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-liftStations',
                generateWlc360iUrl('saas-lift-stations', 'qa'),
            ],
        ], generateStandardUrl('saas-lift-stations', 'qa')),
        customAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_custom_analytics',
                generateWlc360iUrl('saas-custom-analytics', 'qa'),
            ],
        ], generateStandardUrl('saas-custom-analytics', 'qa')),
        // autodeskUser: generateStandardUrl('saas-auth', 'qa'),
        patterns: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-patterns',
                generateWlc360iUrl('saas-pattern', 'stg'),
            ],
        ], generateStandardUrl('saas-pattern', 'qa')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'qa')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.STAGE, 'stg'),
        export: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_export_service',
                generatePlatformUrl('saas-export-service', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-export-service', 'qa')),
        imageManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_image_management', // feature flag name
                generateWlc360iUrl('saas-image-management', 'qa'),
            ],
        ], generateStandardUrl('saas-image-management', 'qa')),
        idp: generateStandardUrl('saas-idp-router', 'qa'),
        serviceIntegration: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_serviceintegration',
                generatePlatformUrl('saas-serviceintegration', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-serviceintegration', 'qa')),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-map-management', 'qa')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'qa')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'qa'),
        sharedTemplates: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'qa'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'qa')),
        sharedTemplatesProxy: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'qa'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'qa')),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'qa'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'qa'),
            ],
        ], generateStandardUrl('saas-favorites', 'qa')),
        userManagement: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-userManagement',
                generatePlatformUrl('saas-user-management', Environments.STAGE, 'stg'),
            ],
            [
                'use-saas-user-management-backend',
                generatePlatformUrl('saas-user-management', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-user-management', 'qa')),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-qa-qa.info360-qa.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-stg-stg-appsync.saas-notification-hub.wlc360p-s-uw2.autodesk.com/graphql',
        edgeAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_edge_analytics',
                generatePlatformUrl('saas-edge-analytics', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-edge-analytics', 'qa')),
        goals: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_goal_management',
                generateWlc360iUrl('saas-goal-management', 'qa'),
            ],
        ], generateStandardUrl('saas-goal-management', 'qa')),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-qa.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'qa'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'qa'),
        modelFactoryCos: generateWaterAIUrl('saas-water-ai-model-factory', Environments.QA, 'stg'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'qa'),
        simulationResults: generateStandardUrl('saas-simulation-results', 'qa'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.QA, 'stg'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.STAGE, 'stg'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.STAGE),
        amso: generateWaterAIUrlCloudOs('amso', Environments.STAGE),
        analysis: generateAnalysisUrl('stg'),
    },
    [Environments.PROD]: {
        alertsManager: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsManager',
                generateWlc360iUrl('saas-alerts-management', 'prod'),
            ],
        ], generateStandardUrl('saas-alerts-management', 'prod')),
        alertsScheduler: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-alertsScheduler',
                generateWlc360iUrl('saas-alerts-scheduler', 'prod'),
            ],
        ], generateStandardUrl('saas-alerts-scheduler', 'prod')),
        analyticFunctions: 'https://saas-analytic-functions.info360-prd.com/prod',
        assets: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-asset-management', 'prod')),
        assetsV2: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-assets',
                generatePlatformUrl('saas-asset-mgmt', Environments.PROD, 'prd').concat('/v2'),
            ],
        ], generateStandardUrl('saas-asset-management', 'prod').concat('/v2')),
        billing: generateStandardUrl('saas-billing', 'prod'),
        edgeManager: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-edgeManager',
                generatePlatformUrl('saas-edge-manager', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-edge-manager', 'prod')),
        entitlements: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-entitlements',
                generatePlatformUrl('saas-auth', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-auth', 'prod')),
        iliEngine: generateStandardUrl('saas-ili-engine', 'prod'),
        iliManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_ili_management', // feature flag name
                generateWlc360iUrl('saas-ili-management', 'prod'),
            ],
        ], generateStandardUrl('saas-ili-management', 'prod')),
        incident: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-incident',
                generateWlc360iUrl('saas-incident-management', 'prod'),
            ],
        ], generateStandardUrl('saas-incident-management', 'prod')),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-prd.com/prod/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'prod'),
        massBalance: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_mass_balance',
                generateWlc360iUrl('saas-mass-balance', 'prod'),
            ],
        ], generateStandardUrl('saas-mass-balance', 'prod')),
        rules: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_rule_management',
                generateWlc360iUrl('saas-rule-management', 'prod'),
            ],
        ], generateStandardUrl('saas-rule-management', 'prod')),
        sensors: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-sensors',
                generateWlc360iUrl('saas-sensor', 'prod'),
            ],
        ], generateStandardUrl('saas-sensor', 'prod')),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-prd.com/prod/'),
        workspaces: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-workspaces',
                generateWlc360iUrl('saas-workspace', 'prd'),
            ],
        ], generateStandardUrl('saas-workspace', 'prod')),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('tiles', 'prod')),
        amUpload: generateStandardUrl('saas-upload-service', 'prod'),
        amInspection: generateStandardUrl('saas-inspection-management', 'prod'),
        superAdmin: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-superAdmin',
                generatePlatformUrl('saas-superadmin', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-superadmin', 'prod')),
        amRisk: generateStandardUrl('saas-risk-management', 'prod'),
        amRiskWss: useFFUrl(featureFlags, [
            [
                'use_wlc360a_saas_asset_web_socket',
                generateAssetWssUrl('saas-asset-web-socket', Environments.PROD, 'prd'),
            ],
        ], generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-prd.com/prod')),
        dataAuthorizer: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data',
                generateWlc360iUrl('saas-info360-sensor-data', 'prod'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com/prod')),
        sensorData: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iHostUrl('saas-info360-sensor-data', 'prod'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com')),
        sensorDataV2: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_info360_sensor_data', // feature flag name
                generateWlc360iUrl('saas-info360-sensor-data', 'prod'),
            ],
        ], generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com/prod')),
        valueMapping: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_value_mapping',
                generatePlatformUrl('saas-value-mapping', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-value-mapping', 'prod')),
        sensorGroup: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_sensor_group', // feature flag name
                generateWlc360iUrl('saas-sensor-group', 'prod'),
            ],
        ], generateStandardUrl('saas-sensor-group', 'prod')),
        liftStations: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-liftStations',
                generateWlc360iUrl('saas-lift-stations', 'prod'),
            ],
        ], generateStandardUrl('saas-lift-stations', 'prod')),
        customAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_custom_analytics',
                generateWlc360iUrl('saas-custom-analytics', 'prod'),
            ],
        ], generateStandardUrl('saas-custom-analytics', 'prod')),
        // autodeskUser: generateStandardUrl('saas-auth', 'prod'),
        patterns: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-patterns',
                generateWlc360iUrl('saas-pattern', 'prd'),
            ],
        ], generateStandardUrl('saas-pattern', 'prod')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'prod')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.PROD, 'prd'),
        export: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_export_service',
                generatePlatformUrl('saas-export-service', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-export-service', 'prod')),
        imageManagement: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_image_management', // feature flag name
                generateWlc360iUrl('saas-image-management', 'prod'),
            ],
        ], generateStandardUrl('saas-image-management', 'prod')),
        idp: generateStandardUrl('saas-idp-router', 'prod'),
        serviceIntegration: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_serviceintegration',
                generatePlatformUrl('saas-serviceintegration', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-serviceintegration', 'prod')),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-map-management', 'prod')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'prod'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'prod')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'prod'),
        sharedTemplates: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'prod'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'prod')),
        sharedTemplatesProxy: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_shared_templates',
                generateWlc360iUrl('saas-shared-templates', 'prod'),
            ],
        ], generateStandardUrl('saas-shared-templates', 'prd')),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'prod'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'prod'),
            ],
        ], generateStandardUrl('saas-favorites', 'prod')),
        userManagement: useFFUrl(featureFlags, [
            [
                'use-wlc360p-url-format-userManagement',
                generatePlatformUrl('saas-user-management', Environments.PROD, 'prd'),
            ],
            [
                'use-saas-user-management-backend',
                generatePlatformUrl('saas-user-management', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-user-management', 'prod')),
        forgeApi: 'https://api.manage.autodesk.com',
        notificationHub: generateNotificationHubAppsyncUrl('saas-notification-hub-appsync-prod-prod'),
        notificationHubPlatform: generatePlatformUrl('saas-notification-hub-prd-prd-appsync.saas-notification-hub', Environments.PROD) + '/graphql',
        edgeAnalytics: useFFUrl(featureFlags, [
            [
                'use_wlc360p_saas_edge_analytics',
                generatePlatformUrl('saas-edge-analytics', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-edge-analytics', 'prod')),
        goals: useFFUrl(featureFlags, [
            [
                'use_wlc360i_saas_goal_management',
                generateWlc360iUrl('saas-goal-management', 'prod'),
            ],
        ], generateStandardUrl('saas-goal-management', 'prod')),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-prd.com'),
        semantic: generateDeviantUrl('https://saas-emagin-semantic.info360|region|-prd.com/prod'),
        optimizationApi: generateDeviantUrl('https://saas-emagin-optimization-api.info360|region|-prd.com/prod'),
        modelFactory: generateDeviantUrl('https://saas-emagin-model-factory.info360|region|-prd.com/prod'),
        modelFactoryCos: generateWaterAIUrl('saas-water-ai-model-factory', Environments.PROD, 'prd'),
        workgroupDatastore: generateDeviantUrl('https://saas-workgroup-datastore.info360|region|-prd.com/prod'),
        simulationResults: generateDeviantUrl('https://saas-simulation-results.info360|region|-prd.com/prod'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.PROD, 'prd'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.PROD, 'prd'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.PROD),
        amso: generateWaterAIUrlCloudOs('amso', Environments.PROD),
        analysis: generateAnalysisUrl('prd'),
    },
});
export const getService = (service, mapOverride = {}, environmentOverride, featureFlags) => {
    const serviceMapClone = cloneDeep(serviceMap(featureFlags));
    const map = merge(serviceMapClone, mapOverride);
    const env = isEnvironment(environmentOverride)
        ? environmentOverride
        : getEnvironment();
    return map[env][service];
};
